import { useCallback, useMemo, useState } from 'react';
import { Dropdown, DropdownButton, DropdownProps, Form, InputGroup } from 'react-bootstrap';
import { useFormContext, Validate } from 'react-hook-form';
import Select, { Props as SelectProps } from 'react-select';
import { Job, RemunerationType } from 'src/models/jobs/job.model';
import { useApiConfiguration } from 'src/ui/shared/hooks/api-configuration.hook';
import { useCombinedFormErrors } from 'src/ui/shared/hooks/combined-form-errors.hook';
import { useStrings } from 'src/ui/shared/hooks/strings.hook';
import { HookControlProps } from '../../hook-form.models';
import { ControlFormGroup } from '../../shared/ControlFormGroup';
import { RangeInputGroupButton } from '../range-input-group/RangeInputGroupButton';
import { rangeInputGroupHelper } from '../range-input-group/range-input-group.constants';
import { useRangeInput } from '../range-input-group/range-input-group.hook';

interface Option {
    label: string;
    value: string;
}

export const useGetCurrencyOption = () => {
    const { includes } = useApiConfiguration();

    const getCurrencyOption = useCallback(
        (currencyCode: string) => {
            return includes.currencies?.find(x => x.code === currencyCode);
        },
        [includes.currencies]
    );

    const options: Option[] = useMemo(() => {
        return (includes.currencies || []).map(x => ({
            label: `${x.name} (${x.code})`,
            value: x.code,
        }));
    }, [includes.currencies]);

    return {
        getCurrencyOption,
        options,
    };
};

const validate: Validate<number | undefined, RelevantFields> = (_, formValues) => {
    const { jobCurrencyRemunerationFrom, jobCurrencyRemunerationTo } = formValues;
    return rangeInputGroupHelper.validate(jobCurrencyRemunerationFrom, jobCurrencyRemunerationTo);
};

const rangedIntervals: Readonly<RemunerationType[]> = [
    'Annual',
    'Monthly',
    'Fortnightly',
    'Weekly',
    'Hourly',
] as const;

type RelevantFields = Pick<
    Job,
    | 'remunerationType'
    | 'jobCurrencyRemunerationFrom'
    | 'jobCurrencyRemunerationTo'
    | 'currencyCode'
>;

const relevantKeys: Readonly<(keyof RelevantFields)[]> = [
    'currencyCode',
    'jobCurrencyRemunerationFrom',
    'jobCurrencyRemunerationTo',
    'remunerationType',
] as const;

type Props = Omit<HookControlProps, 'name'> & {
    initialFrom: number | undefined;
    initialTo: number | undefined;
};

export const RemunerationControl = (props: Props): React.ReactElement => {
    const { watch, setValue } = useFormContext<RelevantFields>();
    const { showRange, onRangeClick, register } = useRangeInput<RelevantFields>(
        props.initialFrom,
        props.initialTo,
        'jobCurrencyRemunerationFrom',
        'jobCurrencyRemunerationTo'
    );

    const { options, getCurrencyOption } = useGetCurrencyOption();
    const {
        job: {
            form: { remuneration },
        },
    } = useStrings();

    const fieldError = useCombinedFormErrors<RelevantFields>(relevantKeys);

    const [interval, currencyCode] = watch([
        'remunerationType',
        'currencyCode',
        'jobCurrencyRemunerationFrom',
        'jobCurrencyRemunerationTo',
    ]);

    const [show, setShow] = useState(false);

    const isRangedIntervalOption = useMemo(() => {
        return rangedIntervals.includes(interval);
    }, [interval]);

    const dropDownVariant = useMemo(() => {
        return fieldError ? 'outline-danger' : 'outline-input';
    }, [fieldError]);
    const inputErrorClass = useMemo(() => {
        return fieldError ? 'border-danger' : '';
    }, [fieldError]);

    const currency = getCurrencyOption(currencyCode);

    const fromPlaceholder = useMemo(() => {
        if (interval === 'Fixed') {
            return interval;
        }

        return `Amount ${interval === 'Annual' ? 'Annually' : interval}`;
    }, [interval]);

    const onToggle: DropdownProps['onToggle'] = (isOpen, e) => {
        setShow(isOpen);
    };
    const onChange: SelectProps<Option, false, never>['onChange'] = (newValue, meta) => {
        if (meta.action === 'select-option' && newValue) {
            setValue('currencyCode', newValue.value);
            setShow(false);
        }
    };

    return (
        <ControlFormGroup
            {...props}
            name="__remuneration"
            popover={remuneration.popover}
            label={remuneration.label}
            helpText="Only the remuneration summary is displayed to students"
            overrideError={fieldError}
        >
            <InputGroup>
                <Dropdown as={InputGroup.Prepend} onToggle={onToggle} show={show}>
                    <Dropdown.Toggle variant={dropDownVariant}>
                        <span className="d-none d-md-inline text-dark">
                            {currency ? `${currency.name} (${currency.code})` : 'Currency'}
                        </span>
                        <span className="d-md-none text-dark">{currency?.code || 'Currency'}</span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="p-0 w-100" show={show}>
                        {show && (
                            <Select
                                autoFocus
                                menuIsOpen
                                onChange={onChange}
                                controlShouldRenderValue={false}
                                placeholder="Filter..."
                                classNames={{
                                    control: () => 'border-0 rounded-0',
                                    menu: () =>
                                        'border-0 rounded-0 shadow-none background-none position-relative p-0 m-0',
                                }}
                                components={{
                                    DropdownIndicator: () => <></>,
                                }}
                                options={options}
                            />
                        )}
                    </Dropdown.Menu>
                </Dropdown>
                <DropdownButton
                    variant={dropDownVariant}
                    as={InputGroup.Prepend}
                    title={<span className="text-dark">{interval}</span>}
                >
                    {rangedIntervals.map(x => (
                        <Dropdown.Item key={x} onClick={() => setValue('remunerationType', x)}>
                            {x}
                        </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => setValue('remunerationType', 'Fixed')}>
                        Fixed amount
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setValue('remunerationType', 'Unpaid')}>
                        Unpaid
                    </Dropdown.Item>
                </DropdownButton>
                {interval !== 'Unpaid' && (
                    <Form.Control
                        tabIndex={-1}
                        placeholder={currency?.symbol}
                        readOnly
                        style={{ minWidth: '25px' }}
                        className={`flex-grow-0 bg-white border-right-0 pr-0 ${inputErrorClass}`}
                    />
                )}
                {(isRangedIntervalOption || interval === 'Fixed') && (
                    <Form.Control
                        {...register('jobCurrencyRemunerationFrom', { validate })}
                        className={`border-left-0 pl-1 ${inputErrorClass}`}
                        placeholder={fromPlaceholder}
                        type="number"
                    />
                )}
                {isRangedIntervalOption && (
                    <>
                        {showRange && (
                            <Form.Control
                                {...register('jobCurrencyRemunerationTo')}
                                placeholder="To"
                                type="number"
                                className={inputErrorClass}
                            />
                        )}
                        <RangeInputGroupButton onClick={onRangeClick} show={showRange} />
                    </>
                )}
                {interval === 'Unpaid' && <Form.Control placeholder={interval} disabled />}
            </InputGroup>
        </ControlFormGroup>
    );
};
